/**
 * Rendering shared by every surface that displays `subscriptionMirrors` rows —
 * the cross-provider console (#1701) and the admin user card (#1705).
 *
 * These lived as module-private helpers inside `SubscriptionsPage` until the
 * user card needed the same cells. They are extracted rather than copied
 * because they encode rules that are wrong by default, and a second
 * independent implementation is exactly how such a rule drifts back:
 *
 * - `NormalizedStateBadge` falls back to the raw value. The frontend type is a
 *   closed union but the wire field is `type: string`, so a provider added
 *   server-side before the frontend union catches up must render as itself
 *   rather than as a blank cell.
 * - `CoverageCell` reports `unmapped` — "grants nothing" — only where
 *   `resolveCatalogCoverage` has established that inference is sound, and
 *   distinguishes a live gap from an ended one. Where the inference is NOT
 *   sound it says `Unknown`, not a dash: a dash beside a red "Not in catalog"
 *   badge in the same column reads as the same answer, and it is the opposite
 *   one.
 *
 * The pure formatters that belong with these live in
 * `lib/utils/subscriptionFormat.ts`; they are split out only because mixing
 * non-component exports into this file breaks fast refresh.
 */

import type { CatalogCoverage } from '../../lib/utils/catalogCoverage';
import {
  NORMALIZED_STATE_LABELS,
  type NormalizedSubscriptionState,
} from '../../lib/types/subscriptionMirror';

const STATE_BADGE_CLASSES: Record<NormalizedSubscriptionState, string> = {
  active:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  grace: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  on_hold: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  paused: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  pending:
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  ended: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  suppressed:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const UNKNOWN_STATE_CLASSES =
  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

/**
 * Explanations for states whose label does not carry its own meaning.
 *
 * `suppressed` is the one an admin cannot possibly infer: it appeared on both
 * surfaces with nothing to say what it was. It is also NOT a synonym for ended
 * — `applyWebhookEvent` pins a suppressed doc at `ECHO_SUPPRESSED`, so one that
 * later genuinely ends looks identical to one still live, which is why the card
 * does not file it under past subscriptions.
 */
const STATE_TITLES: Partial<Record<NormalizedSubscriptionState, string>> = {
  // No issue numbers in user-facing copy: an admin answering a support ticket
  // has no way to look one up and no reason to.
  //
  // The mechanism here was also wrong until now. It said the echo was
  // "suppressed so it could not overwrite what we hold";
  // `services/external/echoDetection.ts` says we copy a user's Stripe access
  // OUT into Supapass, Supapass reports the copy back, and the risk is "a
  // phantom ACTIVE record [that] keeps granting access after the user stops
  // paying" — not an overwrite.
  suppressed:
    "We copied this user's Stripe subscription into Supapass, and Supapass is reporting the copy back. Kept for the record, but it grants nothing — the Stripe row is the real one.",
  grace: 'A payment failed and is being retried. Access is kept during this.',
  on_hold: 'A payment failed and the retries ran out, so it is suspended.',
  pending: 'Recorded but not in effect yet.',
};

/** The cross-provider normalized state, badged. Falls back to the raw value. */
export function NormalizedStateBadge({
  state,
  rawStatus,
}: {
  state: NormalizedSubscriptionState;
  /**
   * The provider's own word. Surfaces are free to hide it when it merely
   * restates the label; passing it here keeps it one hover away rather than
   * gone.
   */
  rawStatus?: string;
}) {
  const explanation = STATE_TITLES[state];
  const raw = rawStatus ? `Provider's own word: "${rawStatus}".` : '';
  const title = [explanation, raw].filter(Boolean).join(' ') || undefined;
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        STATE_BADGE_CLASSES[state] ?? UNKNOWN_STATE_CLASSES
      }`}
    >
      {NORMALIZED_STATE_LABELS[state] ?? state}
    </span>
  );
}

/**
 * What the environment's catalog says this subscription grants.
 *
 * `unmapped` is only ever reported for a provider whose catalog is exhaustive
 * (see `resolveCatalogCoverage`); `actionable` distinguishes a live one, where
 * somebody is affected right now, from an ended one where nobody is.
 */
export function CoverageCell({
  coverage,
  actionable,
  dense = false,
}: {
  coverage: CatalogCoverage;
  actionable: boolean;
  /**
   * Put the entitlement ids in the cell's tooltip instead of on a second line.
   *
   * For the console, where this cell carries the SAME two lines on 19 of 28
   * rows: the repetition sets the row height for the whole table while telling
   * a reader nothing that varies. The user card leaves them visible — a handful
   * of rows, and the id is what the access lines beside it are keyed on.
   */
  dense?: boolean;
}) {
  if (coverage.state === 'mapped') {
    const ids = coverage.entitlements.join(', ');
    if (dense) {
      return (
        <span className="text-gray-700 dark:text-gray-200" title={ids}>
          {coverage.label}
        </span>
      );
    }
    return (
      <div>
        <span className="text-gray-700 dark:text-gray-200">
          {coverage.label}
        </span>
        <div className="text-xs text-gray-400">{ids}</div>
      </div>
    );
  }
  if (coverage.state === 'unmapped') {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          actionable
            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        }`}
        title={
          actionable
            ? "This product isn't in the catalog, so the subscription grants nothing. Copy the product id from this row and send it to engineering — the catalog can't be edited from this tool."
            : "This product isn't in the catalog, but the subscription isn't live, so nobody is affected."
        }
      >
        Not in catalog
      </span>
    );
  }
  return (
    <span
      className="text-xs text-gray-400 dark:text-gray-500 italic"
      title={coverage.reason}
    >
      Unknown
    </span>
  );
}
